// src/App.js

import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import { ErrorBoundary } from './components/ErrorBoundary';
import ErrorDialogProvider from './providers/ErrorDialogProvider';
import LoginRedirect from './components/LoginRedirect';
import AdminLogin from './pages/admin/AdminLogin';
import AgencyLogin from './pages/agency/AgencyLogin';
import SolicitorLogin from './pages/solicitor/SolicitorLogin';
import NotFound from './pages/NotFound';
import Error from './pages/Error';
import PasswordReset from './pages/PasswordReset';
import PasswordResetRequest from './pages/PasswordResetRequest';
import AgencySearch from './pages/admin/AgencySearch';
import AccountCreate from './pages/admin/AccountCreate';
import AccountList from './pages/admin/AccountList';
import AccountEdit from './pages/admin/AccountEdit';
import AgencyRegistration from './pages/admin/AgencyRegistration';
import AgencyDetail from './pages/admin/AgencyDetail';
import AgencyEdit from './pages/admin/AgencyEdit';
import InsuranceUpload from './pages/admin/InsuranceUpload';
import InsuranceApplicationList from './pages/admin/InsuranceApplicationList';
import NoticeList from './pages/admin/NoticeList';
import NoticeForm from './pages/admin/NoticeForm';
import GuardControlSeihoHikiukeList from './pages/admin/GuardControlSeihoHikiukeList';
import AgencyAccountEdit from './pages/agency/AccountEdit';
import AgencyAgencyDetail from './pages/agency/AgencyDetail';
import AgencyAgencyEdit from './pages/agency/AgencyEdit';
import AgencyAgencyRegistration from './pages/agency/AgencyRegistration';
import AgencyAgencySearch from './pages/agency/AgencySearch';
import AgencyInsuranceUpload from './pages/agency/InsuranceUpload';
import SolicitorAccountEdit from './pages/solicitor/AccountEdit';
import InsuranceSystemSearch from './pages/solicitor/InsuranceSystemSearch';
import SolicitorSystemDetail from './pages/solicitor/InsuranceSystemDetail';
import InsuranceHikiukeDetail from './pages/solicitor/InsuranceHikiukeDetail';
import SearchHistory from './pages/solicitor/SearchHistory';
import DocumentCatalogSearch from './pages/solicitor/DocumentCatalogSearch';
import SystemEntry from './pages/SystemEntry';

import './styles/App.css';
import './styles/component.css';

/**
 * ProtectedRoute:
 * - allowedRoles: ['admin','agency','solicitor'] のいずれかに userType が含まれているかチェック
 * - accessCondition: (userType, userInfo) => boolean を渡すと、追加のアクセス可否判定ができる
 */
const ProtectedRoute = ({
  children,
  allowedRoles,
  accessCondition
}) => {
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('token');
  const userType       = localStorage.getItem('userType');
  const userInfo       = JSON.parse(localStorage.getItem('userInfo') || '{}');
  
  // 未認証ならURLに応じて適切なログイン画面へ
  if (!isAuthenticated) {
    const currentPath = location.pathname;
    
    // URLパスに応じてログイン画面を振り分け
    if (currentPath.startsWith('/admin/')) {
      return <Navigate to="/admin/login" replace />;
    } else if (currentPath.startsWith('/agency/')) {
      return <Navigate to="/agency/login" replace />;
    } else if (currentPath.startsWith('/solicitor/')) {
      return <Navigate to="/solicitor/login" replace />;
    } else {
      // デフォルトは汎用管理者ログイン画面
      return <Navigate to="/admin/login" replace />;
    }
  }
  
  // allowedRoles があればロールチェック
  if (allowedRoles && !allowedRoles.includes(userType)) {
    // 管理者なのに allowedRoles にないなら管理者用検索へ
    if (userType === 'admin') {
      return <Navigate to="/admin/agency/search" replace />;
    }
    // 代理店なのに allowedRoles にないなら詳細 or ホームへ
    if (userType === 'agency') {
      const agencyId = userInfo.agency_id;
      if (agencyId) {
        return <Navigate to={`/agency/detail/${agencyId}`} replace />;
      }
      return <Navigate to="/" replace />;
    }
    // 募集人なら募集人用検索へ
    if (userType === 'solicitor') {
      return <Navigate to="/solicitor/insurance/system/search" replace />;
    }
  }
  
  // accessCondition があれば追加判定
  if (accessCondition && !accessCondition(userType, userInfo)) {
    // 条件を満たさない場合はホームへ
    return <Navigate to="/" replace />;
  }
  
  // ここまで通れば許可
  return children;
};


function App() {
  return (
    <ErrorBoundary>
      <ErrorDialogProvider>
        <Router>
            <Routes>
              {/* 公開ルート */}
              <Route path="/login" element={<LoginRedirect />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/agency/login" element={<AgencyLogin />} />
              <Route path="/solicitor/login" element={<SolicitorLogin />} />
              <Route path="/password-reset-request" element={<PasswordResetRequest />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route path="/system-entry" element={<SystemEntry />} />
              <Route path="/error" element={<Error />} />

              {/* 管理者用代理店検索（純粋管理者向け） */}
              <Route
                path="/admin/agency/search"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AgencySearch />
                  </ProtectedRoute>
                }
              />

              {/* 管理者アカウント管理 */}
              <Route
                path="/admin/account/create"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AccountCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/account/list"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AccountList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/account/edit/:userId?"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AccountEdit />
                  </ProtectedRoute>
                }
              />

              {/* 管理者代理店管理 */}
              <Route
                path="/admin/agency/registration/:agencyId?"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AgencyRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/agency/detail/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AgencyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/agency/edit/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AgencyEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/agency/insurance/upload/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <InsuranceUpload />
                  </ProtectedRoute>
                }
              />

              {/* 管理者保険申請管理 */}
              <Route
                path="/admin/insurance/applications"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <InsuranceApplicationList />
                  </ProtectedRoute>
                }
              />

              {/* 管理者おしらせ管理 */}
              <Route
                path="/admin/notices"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <NoticeList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notices/new"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <NoticeForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notices/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <NoticeForm />
                  </ProtectedRoute>
                }
              />

              {/* 管理者引受目安ガード制御 */}
              <Route
                path="/admin/guard-control/seiho-hikiuke"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <GuardControlSeihoHikiukeList />
                  </ProtectedRoute>
                }
              />

              {/* 代理店検索ルート */}
              <Route
                path="/agency/search"
                element={
                  <ProtectedRoute
                    allowedRoles={['admin', 'agency']}
                    accessCondition={(userType, userInfo) => {
                      // 管理者兼代理店ユーザ
                      if (userType === 'admin' && userInfo.agency_id) {
                        return true;
                      }
                      // 代理店ユーザで agency_id が null
                      if (userType === 'agency' && !userInfo.agency_id) {
                        return true;
                      }
                      return false;
                    }}
                  >
                    <AgencyAgencySearch />
                  </ProtectedRoute>
                }
              />

              {/* 代理店ルート */}
              <Route
                path="/agency/account/edit"
                element={
                  <ProtectedRoute allowedRoles={['agency']}>
                    <AgencyAccountEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency/detail/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['agency']}>
                    <AgencyAgencyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency/edit/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['agency']}>
                    <AgencyAgencyEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency/registration/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['agency']}>
                    <AgencyAgencyRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency/insurance/upload/:agencyId"
                element={
                  <ProtectedRoute allowedRoles={['agency']}>
                    <AgencyInsuranceUpload />
                  </ProtectedRoute>
                }
              />

              {/* 募集人ルート */}
              <Route
                path="/solicitor/account/edit"
                element={
                  <ProtectedRoute allowedRoles={['solicitor', 'agency']}>
                    <SolicitorAccountEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/solicitor/insurance/system/search"
                element={
                  <ProtectedRoute allowedRoles={['solicitor', 'agency']}>
                    <InsuranceSystemSearch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/solicitor/insurance/system/detail"
                element={
                  <ProtectedRoute allowedRoles={['solicitor', 'agency']}>
                    <SolicitorSystemDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/solicitor/insurance/hikiuke/detail"
                element={
                  <ProtectedRoute allowedRoles={['solicitor', 'agency']}>
                    <InsuranceHikiukeDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/solicitor/search/history"
                element={
                  <ProtectedRoute allowedRoles={['solicitor', 'agency']}>
                    <SearchHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/solicitor/document-catalog"
                element={
                  <ProtectedRoute allowedRoles={['solicitor', 'agency']}>
                    <DocumentCatalogSearch />
                  </ProtectedRoute>
                }
              />

              {/* デフォルトルート */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              {/* 404ページ - 既存のNotFoundコンポーネントを使用 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
        </Router>
      </ErrorDialogProvider>
    </ErrorBoundary>
  );
}

export default App;
